import { Card } from '@/components/ui/card';
import { PortfolioStatistics } from '@/services/dashboard.service';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '@/utils/helpers';

const KeyMetricsGrid = ({ data }: { data?: PortfolioStatistics }) => {
  const totalOutstandingBalance = Number(data?.total_outstanding_balance);
  const minPrice = data?.min_price;
  const maxPrice = data?.max_price;
  const avgPrice = data?.avg_price;
  const minWal = data?.min_wal;
  const maxWal = data?.max_wal;
  const avgWal = data?.avg_wal;
  const minDuration = data?.min_modified_duration;
  const maxDuration = data?.max_modified_duration;
  const avgDuration = data?.avg_modified_duration;

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
      data-test-id="key-metrics-grid"
    >
      <Card className="p-6" data-test-id="total-outstanding-balance-card">
        <div
          className="flex items-center justify-between"
          data-test-id="card-content"
        >
          <div data-test-id="metric-info">
            <p
              className="text-sm font-medium text-gray-600"
              data-test-id="metric-label"
            >
              Total Fair Value
            </p>
            <p
              className="text-lg font-bold text-gray-900"
              data-test-id="metric-value"
            >
              {formatCurrency(totalOutstandingBalance, 0)}
            </p>
          </div>
          <div
            className="p-3 bg-blue-100 rounded-full"
            data-test-id="metric-icon"
          >
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              data-test-id="metric-icon-svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              />
            </svg>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-test-id="price-range-card">
        <div
          className="flex items-center justify-between"
          data-test-id="card-content"
        >
          <div data-test-id="metric-info">
            <p
              className="text-sm font-medium text-gray-600"
              data-test-id="metric-label"
            >
              Price
            </p>
            <p
              className="text-lg font-bold text-gray-900"
              data-test-id="metric-value"
            >
              {formatPercentage(Number(avgPrice) * 100, 3)}
            </p>
            <p className="text-xs text-gray-500" data-test-id="metric-subvalue">
              {`${formatPercentage(Number(minPrice) * 100, 3)} - ${formatPercentage(Number(maxPrice) * 100, 3)}`}
            </p>
          </div>
          <div
            className="p-3 bg-green-100 rounded-full"
            data-test-id="metric-icon"
          >
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              data-test-id="metric-icon-svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              />
            </svg>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-test-id="wal-range-card">
        <div
          className="flex items-center justify-between"
          data-test-id="card-content"
        >
          <div data-test-id="metric-info">
            <p
              className="text-sm font-medium text-gray-600"
              data-test-id="metric-label"
            >
              WAL
            </p>
            <p
              className="text-lg font-bold text-gray-900"
              data-test-id="metric-value"
            >
              {`${formatNumber(avgWal, 2)} years`}
            </p>
            <p className="text-xs text-gray-500" data-test-id="metric-subvalue">
              {`${formatNumber(minWal, 2)} - ${formatNumber(maxWal, 2)} years`}
            </p>
          </div>
          <div
            className="p-3 bg-yellow-100 rounded-full"
            data-test-id="metric-icon"
          >
            <svg
              className="w-6 h-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              data-test-id="metric-icon-svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-test-id="duration-range-card">
        <div
          className="flex items-center justify-between"
          data-test-id="card-content"
        >
          <div data-test-id="metric-info">
            <p
              className="text-sm font-medium text-gray-600"
              data-test-id="metric-label"
            >
              Modified Duration
            </p>
            <p
              className="text-lg font-bold text-gray-900"
              data-test-id="metric-value"
            >
              {`${formatNumber(avgDuration, 2)} years`}
            </p>
            <p className="text-xs text-gray-500" data-test-id="metric-subvalue">
              {`${formatNumber(minDuration, 2)} - ${formatNumber(maxDuration, 2)} years`}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default KeyMetricsGrid;
